import { createSelector } from '@ngrx/store';
import { createSlice, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';
import {
  ApiErrorResponse,
  ApiErrorResponseWithId,
} from 'src/app/shared/interfaces/api-error-response';

import {
  ProductAdminResponse,
  ProductDetailedResponse,
  StatusUpdateRequest,
} from '@app/api/marketplace/models';
import { Unit } from '@products/interfaces/models';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { ContentStatus } from '@shared/interfaces/content-status';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

export interface ProductAdminPageRequest extends PageRequest {
  name?: string;
  brand?: string;
  merchantName?: string;
  userName?: string;
  created?: Unit;
  status?: ContentStatus;
}

export interface State {
  // products - get
  productsFilter: ProductAdminPageRequest;
  productsLoadError: boolean;
  productsLoading: boolean;
  products: EntityState<ProductAdminResponse>;
  activeProduct: ProductDetailedResponse | undefined;
  activeProductLoading: boolean;
  activeProductLoadError: boolean;
  totalProducts: number;
  productSaving: boolean;
  productSaveError?: ApiErrorResponse;
  productStatusSaving?: boolean;
  productStatusSaveError?: ApiErrorResponse;
}

export const productAdapter = createSliceEntityAdapter<ProductAdminResponse>({
  selectId: (a) => a.packagingUnitId ?? '',
});

export const initialState: State = {
  productsFilter: {
    status: ContentStatus.Draft,
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  productsLoading: false,
  productsLoadError: false,
  products: productAdapter.getInitialState(),
  activeProduct: undefined,
  activeProductLoading: false,
  activeProductLoadError: false,
  totalProducts: 0,
  productSaving: false,
  productSaveError: undefined,
  productStatusSaving: false,
  productStatusSaveError: undefined,
};

export const {
  actions: ProductApprovalsActions,
  selectors,
  ...ProductApprovalsFeature
} = createSlice({
  name: 'product-approvals',
  initialState,
  reducers: {
    loadProducts: {
      trigger: (state, filter?: PayloadAction<ProductAdminPageRequest>) => {
        productAdapter.setAll(state.products, []);
        state.productsFilter = filter ?? state.productsFilter;
        state.productsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<PageResponse<ProductAdminResponse>>
      ) => {
        productAdapter.setAll(state.products, action.content);
        state.totalProducts = action?.totalElements ?? 0;
        state.productsLoading = false;
        state.productsLoadError = false;
      },
      failure: (state) => ({
        ...state,
        productsLoading: false,
        productsLoadError: true,
      }),
    },
    loadActiveProduct: {
      trigger: (
        state,
        action: PayloadAction<{ packagingUnitId: string }>
      ): State => ({
        ...state,
        ...action,
        activeProductLoading: true,
      }),
      success: (state, action: PayloadAction<ProductDetailedResponse>) => {
        state.activeProduct = action;
        state.activeProductLoading = false;
        state.activeProductLoadError = false;
      },
      failure: (state) => ({
        ...state,
        activeProductLoadError: true,
        activeProductLoading: false,
      }),
    },
    saveProduct: {
      trigger: (state, _action: PayloadAction<ProductDetailedResponse>) => {
        state.productSaving = true;
      },
      success: (state, action: PayloadAction<ProductDetailedResponse>) => {
        productAdapter.upsertOne(
          state.products,
          action as unknown as ProductAdminResponse
        );
        state.productSaving = false;
        state.productSaveError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.productSaving = false;
        state.productSaveError = action;
      },
    },
    updateProductStatus: {
      trigger: (
        state,
        _action: PayloadAction<{ updateRequest: StatusUpdateRequest }>
      ) => {
        state.productStatusSaving = true;
      },
      success: (state) => {
        state.productStatusSaving = false;
        state.productStatusSaveError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.productStatusSaving = false;
        state.productStatusSaveError = action;
      },
    },
    reset: (_) => initialState,
  },
});

export const ProductApprovalsSelectors = {
  ...selectors,
  selectProductList: createSelector(selectors.selectProducts, (products) =>
    Object.values(products.entities)
  ),
};
